import { get } from "@github/webauthn-json/browser-ponyfill";
import ReCAPTCHA, { ReCAPTCHAHandle } from "@steamer-academy/react-google-recaptcha";
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { authApi } from "../api/auth";
import PasskeyIcon from "../assets/PasskeyIcon";
import logo from "../assets/hourglass-logo-brown.svg";
import "../css/googleSignin.css";
import { getAppleAuthUrl } from "../helpers/appleSignin";
import { HGBugsnagNotify } from "../helpers/bugsnag";
import { ErrType, getErrorType, getStatusCode } from "../helpers/errors";
import HourglassGlobals from "../helpers/globals";
import { getGoogleAuthUrl } from "../helpers/googleSignin";
import {
  hasPasskeyHint,
  markPasskeySuggested,
  passkeysSupported,
  registerPasskey,
  setPasskeyHint,
  shouldSuggestPasskey,
} from "../helpers/passkeys";
import useInterval from "../helpers/useInterval";
import { getCookie } from "../helpers/util";
import {
  LoginCredentials,
  TwoFactorChallenge,
  TwoFactorChallengeResponse,
  TwoFactorChallengeStatusRequest,
} from "../types/auth";
import { ForgotPWRequest } from "../types/forgotpw";
import { ValidationErrorDetail } from "../types/validation";
import { Danger } from "./alerts";
import { GoogleSignInButton } from "./buttons";
import { redirectToZoneRegistrationSteps, setSMPWRegMode } from "./register/smpwRegistration";

type LoginProps = {
  setHaveToken: (have: boolean) => void;
  forSmpwReg?: boolean;
};

export type LoginLocationState = {
  email: string;
  googleAuth: boolean;
  appleAuth: boolean;
};

//backend state times out after 20 minutes
export const StateTimeoutSeconds = 19 * 60 + 30;

export default function HourglassLogin(props: LoginProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginFailureMessage, setLoginFailureMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [recaptchaRequired, setRecaptchaRequired] = useState(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState("");
  const recaptchaRef = useRef<ReCAPTCHAHandle>(null);
  const [showStateTimeout, setShowStateTimeout] = useState(false);
  const [passkeySupport, setPasskeySupport] = useState(false);
  const passkeyAbortRef = useRef<AbortController>(null);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge>();
  const [showPasskeySuggestion, setShowPasskeySuggestion] = useState(false);

  useEffect(() => {
    passkeysSupported().then((supported) => setPasskeySupport(supported));
  }, []);

  if (!email && location.state) {
    if (location.state.email && !location.state.googleAuth && !location.state.appleAuth) {
      setEmail(location.state.email);
    }
  }

  const updateEmail = (e: any) => {
    setEmail(e.target.value);
    setLoginFailureMessage("");
  };
  const updatePassword = (e: any) => {
    setPassword(e.target.value);
    setLoginFailureMessage("");
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStateTimeout(true), StateTimeoutSeconds * 1000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const doPasskeyLogin = useCallback(
    async (conditional = true) => {
      if (!passkeySupport) return;
      passkeyAbortRef.current?.abort();
      const abortController = new AbortController();
      passkeyAbortRef.current = abortController;

      try {
        const credentialRequestOptions = await authApi.webauthnBeginLogin();
        credentialRequestOptions.mediation = conditional ? "conditional" : "optional";
        credentialRequestOptions.signal = abortController.signal;
        const credential = await get(credentialRequestOptions);
        await authApi.webauthnFinishLogin(credential);
        setPasskeyHint();
        props.setHaveToken(true);
      } catch (err: any) {
        switch (err.name) {
          case "NotAllowedError":
            // user canceled
            break;
          case "OperationError":
            // usually seen in dev when component is mounted multiple times
            break;
          case "AbortError":
            // request was aborted (previous attempt canceled when starting a new one)
            break;
          case "TypeError":
            // bad/missing response
            break;
          default: {
            const statusCode = getStatusCode(err);
            switch (statusCode) {
              case 401:
                if (conditional) {
                  // challenge likely expired; silently retry with a fresh one
                  doPasskeyLogin(true).then();
                } else {
                  console.warn("webauthn 401", err);
                  setLoginFailureMessage(t("login.error.bad-credentials"));
                }
                break;
              case 429:
                setLoginFailureMessage(t("form.error.try-later"));
                break;
              default:
                console.error("webauthn login error", err, err.name);
                HGBugsnagNotify("loginWebAuthn", err);
            }
          }
        }
      }
    },
    [passkeySupport, t, props],
  );

  const doAutoLogin = useCallback(() => {
    // auto-login: check for cookies that are hints to try google or apple login
    // if they aren't there, then attempt passkey
    try {
      const googleSub = getCookie(HourglassGlobals.GoogleSubCookie);
      if (googleSub && googleSub.length > 1) {
        window.location.href = getGoogleAuthUrl(
          window.HGGlobal?.state || "state",
          HourglassGlobals.oAuthRedirectURI,
          window.HGGlobal.nonce || "nonce",
          null,
          googleSub,
          false,
          window.HGGlobal?.codeChallenge,
          window.HGGlobal?.codeChallengeMethod,
        );
      } else {
        const appleSub = getCookie(HourglassGlobals.AppleSubCookie);
        if (appleSub && appleSub.length > 1) {
          window.location.href = getAppleAuthUrl(
            window.HGGlobal?.state || "state",
            HourglassGlobals.appleAuthRedirectURI,
            window.HGGlobal.nonce || "nonce",
          );
        } else if (hasPasskeyHint()) {
          doPasskeyLogin(true).then();
        }
      }
    } catch (err) {
      console.warn("error checking for auto-google-signin cookie", err);
    }
  }, [doPasskeyLogin]);

  useEffect(() => {
    // start auto-login after a short delay
    // this helps smooth out bursty requests
    const timer = setTimeout(doAutoLogin, 150);
    return () => {
      clearTimeout(timer);
    };
  }, [doAutoLogin]); // doAutoLogin only changes when doPasskeyLogin (i.e. passkeySupport) changes

  const submitLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.checkValidity()) return;

    const credentials: LoginCredentials = {
      email: email,
      password: password,
      recaptchaResponse: recaptchaResponse,
    };
    setTwoFactorChallenge(undefined);
    setTwoFactorRequired(false);

    try {
      const loginResp = await authApi.passwordLogin(credentials);
      if (loginResp.twoFactorChallenge) {
        setTwoFactorChallenge(loginResp.twoFactorChallenge);
        setTwoFactorRequired(true);
      } else {
        props.setHaveToken(true);
        if (props.forSmpwReg) {
          redirectToZoneRegistrationSteps();
        }
      }
    } catch (err: any) {
      const statusCode = getStatusCode(err);
      switch (statusCode) {
        case 403:
          // this means we've tried too often and need to solve a recaptcha
          setRecaptchaRequired(true);
          setLoginFailureMessage(t("form.recaptcha-error"));
          break;
        case 429:
          // rate limiting has been hit
          setLoginFailureMessage(t("form.error.try-later"));
          break;
        default:
          if (recaptchaRequired) recaptchaRef.current?.reset();
          setLoginFailureMessage(t("login.error.bad-credentials"));
      }
    }
  };

  return (
    <Container fluid="md" className="min-vh-100 d-flex align-items-center justify-content-center py-5">
      <div className="w-100">
        <StateTimeoutModal show={showStateTimeout} setShow={setShowStateTimeout} />
        <ForgotPasswordModal show={showForgotPassword} setShow={setShowForgotPassword} />
        <PasskeySuggestionModal
          show={showPasskeySuggestion}
          onComplete={() => {
            setShowPasskeySuggestion(false);
            props.setHaveToken(true);
            if (props.forSmpwReg) {
              redirectToZoneRegistrationSteps();
            }
          }}
        />
        {!!twoFactorChallenge && (
          <TwoFactorModal
            challenge={twoFactorChallenge}
            show={twoFactorRequired}
            setShow={(show: boolean) => {
              setTwoFactorRequired(show);
              if (!show) {
                setTwoFactorChallenge(undefined);
                setTwoFactorRequired(false);
              }
            }}
            setSuccess={async (success: boolean) => {
              setTwoFactorChallenge(undefined);
              setTwoFactorRequired(false);
              if (success) {
                if (passkeySupport && shouldSuggestPasskey()) {
                  try {
                    const passkeys = await authApi.getPasskeys();
                    if (passkeys.length === 0) {
                      markPasskeySuggested();
                      setShowPasskeySuggestion(true);
                      return;
                    }
                  } catch {
                    // proceed with login if passkey check fails
                  }
                }
                props.setHaveToken(true);
                if (props.forSmpwReg) {
                  redirectToZoneRegistrationSteps();
                }
              }
            }}
            setChallenge={setTwoFactorChallenge}
            loginEmail={email}
          />
        )}

        <Row className="justify-content-center mt-4 mb-5">
          <Col lg={4} md={5} sm={8} className="text-center">
            <div className="mb-3">
              <img src={logo} className="App-logo" alt="logo" />
            </div>
            <h4 className="App-logo-text fw-light text-muted mb-4">{t("login.message.signin")}</h4>
          </Col>
        </Row>

        <Form onSubmit={submitLogin}>
          <Row className="justify-content-center mt-2">
            <Col lg={4} md={5} sm={8}>
              <Form.Group controlId="email" className="mb-3">
                <Form.Label className="fw-medium text-muted">{t("form.label.email-address")}</Form.Label>
                <Form.Control
                  required
                  type="email"
                  name="email"
                  value={email}
                  onChange={updateEmail}
                  autoComplete="username webauthn"
                  className="py-2 border-2"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={4} md={5} sm={8}>
              <Form.Group controlId="password" className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="fw-medium text-muted mb-0">{t("form.label.password")}</Form.Label>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-decoration-none fw-normal text-primary border-0"
                    onClick={() => setShowForgotPassword(true)}
                    tabIndex={-1}
                  >
                    {t("form.label.i-forgot")}
                  </Button>
                </div>
                <Form.Control
                  required
                  type="password"
                  name="password"
                  value={password}
                  onChange={updatePassword}
                  autoComplete="current-password webauthn"
                  className="py-2 border-2"
                />
                {!!loginFailureMessage && (
                  <Alert variant="danger" className="mt-2 py-2">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {loginFailureMessage}
                    </small>
                  </Alert>
                )}
              </Form.Group>

              {recaptchaRequired && (
                <div className="d-flex justify-content-center mb-3">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={window.HGGlobal?.recaptchaSiteKey || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                    onChange={(value: string | null) => setRecaptchaResponse(value || "")}
                  />
                </div>
              )}

              <Button
                className="mt-3 w-100 py-2 fw-medium"
                variant="primary"
                type="submit"
                disabled={recaptchaRequired && !recaptchaResponse}
                style={{ transition: "all 0.15s ease-in-out" }}
                onClick={() => {
                  if (props.forSmpwReg) setSMPWRegMode();
                }}
              >
                {t("form.button.login")}
              </Button>

              {passkeySupport && (
                <Button
                  className="w-100 mt-3 py-2 fw-medium"
                  variant="outline-primary"
                  onClick={() => {
                    if (props.forSmpwReg) setSMPWRegMode();
                    doPasskeyLogin(false);
                  }}
                  style={{ transition: "all 0.15s ease-in-out" }}
                >
                  <PasskeyIcon size="1.5em" className="me-2" />
                  {t("login.sign-in-with-passkey")}
                </Button>
              )}

              {props.forSmpwReg && (
                <Button
                  className="w-100 mt-3 py-2 fw-medium"
                  variant="outline-primary"
                  onClick={() => (window.location.href = "/v2/page/app/smpwuserreg")}
                  style={{ transition: "all 0.15s ease-in-out" }}
                >
                  {t("login.link.sign-up")}
                </Button>
              )}
            </Col>
          </Row>
        </Form>

        <Row className="justify-content-center mt-4 mb-5">
          <Col lg={4} md={5} sm={8} className="text-center">
            <div className="position-relative mb-4">
              <hr className="text-muted" />
              <span
                className="position-absolute top-50 start-50 translate-middle px-3 text-muted small fw-medium text-center"
                style={{ backgroundColor: "var(--bs-body-bg, #fff)" }}
              ></span>
            </div>
            <Row className="d-flex gap-3 justify-content-center">
              <Col xs={12} className="d-flex justify-content-center">
                <GoogleSignInButton forSmpwReg={props.forSmpwReg} />
              </Col>
              <Col xs={12} className="d-flex justify-content-center">
                <Button
                  href={getAppleAuthUrl(
                    window.HGGlobal?.state || "state",
                    HourglassGlobals.appleAuthRedirectURI,
                    window.HGGlobal?.nonce || "nonce",
                  )}
                  onClick={() => {
                    if (props.forSmpwReg) setSMPWRegMode();
                  }}
                  variant="dark"
                  className="w-100 d-flex align-items-center justify-content-center py-2 apple-signin-btn"
                  style={{
                    height: "44px",
                    backgroundColor: "#000000",
                    borderColor: "#000000",
                    color: "#ffffff",
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", Helvetica, Arial, sans-serif',
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.15s ease-in-out",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="me-2">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  {t("login.sign-in-apple")}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Container>
  );
}

type ForgotPasswordModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
};

function ForgotPasswordModal(props: ForgotPasswordModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const emailFormRef = useRef<HTMLFormElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [recaptchaResponse, setRecaptchaResponse] = useState("");
  const [isGoogle, setIsGoogle] = useState(false);
  const [isApple, setIsApple] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    props.setShow(false);
    setEmail("");
    setRecaptchaResponse("");
    setIsGoogle(false);
    setIsApple(false);
    setSuccess(false);
  };

  const changeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value);
    e.currentTarget.setCustomValidity("");
  };

  const recaptchaChange = (value: string | null) => {
    setRecaptchaResponse(value || "");
  };

  const doForgot = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;

    if (!form.checkValidity()) return;
    if (!recaptchaResponse) return;

    const req: ForgotPWRequest = {
      email: email,
      recaptchaResponse: recaptchaResponse,
    };
    try {
      const resp = await authApi.forgotPassword(req);
      if (resp.google) {
        setIsGoogle(true);
      } else if (resp.apple) {
        setIsApple(true);
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      const errType = await getErrorType(err);
      if (errType !== ErrType.Validation) return;
      const respData = await err.response.clone().json();
      if (Array.isArray(respData?.errorDetails)) {
        if (respData.errorDetails.find((ve: ValidationErrorDetail) => ve.name === "email")) {
          // client was ok with email, but server didn't like it (slightly different validation logic)
          emailRef.current?.setCustomValidity(t("userauth.feedback.invalid-email"));
          emailFormRef.current?.reportValidity();
        }
      }
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose} size="sm" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-medium">{t("form.reset-password")}</Modal.Title>
      </Modal.Header>
      <Form ref={emailFormRef} onSubmit={doForgot}>
        <Modal.Body className="pt-2">
          {!success && !isGoogle && !isApple && (
            <div>
              <p className="text-muted mb-3">{t("login.modal.body.reset-password.0")}</p>
              <Form.Group className="mb-3">
                <Form.Label className="fw-medium text-muted">{t("form.label.email-address")}</Form.Label>
                <Form.Control
                  ref={emailRef}
                  required
                  type="email"
                  value={email}
                  onChange={changeEmail}
                  className="py-2"
                />
              </Form.Group>
              <div className="d-flex justify-content-center">
                <ReCAPTCHA
                  sitekey={window.HGGlobal?.recaptchaSiteKey || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                  onChange={recaptchaChange}
                />
              </div>
            </div>
          )}
          {success && (
            <div className="text-center p-3">
              <div className="mb-3">
                <i className="fas fa-check-circle text-success" style={{ fontSize: "3rem" }}></i>
              </div>
              <p className="text-success fw-medium">{t("login.modal.body.reset-password.success")}</p>
            </div>
          )}
          {isGoogle && (
            <div className="text-center p-3">
              <div className="mb-3">
                <i className="fab fa-google text-primary" style={{ fontSize: "3rem" }}></i>
              </div>
              <p className="text-muted">{t("login.modal.body.reset-password.google")}</p>
            </div>
          )}
          {isApple && (
            <div className="text-center p-3">
              <div className="mb-3">
                <i className="fab fa-apple text-dark" style={{ fontSize: "3rem" }}></i>
              </div>
              <p className="text-muted">{t("login.sign-in-apple")}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          {!success && !isGoogle && (
            <Button
              variant="primary"
              type="submit"
              disabled={!email || !recaptchaResponse}
              className="w-100 py-2 fw-medium"
              style={{
                transition: "all 0.15s ease-in-out",
              }}
            >
              {t("form.reset-password")}
            </Button>
          )}
          {(success || isGoogle || isApple) && (
            <Button variant="primary" onClick={handleClose} className="w-100 py-2 fw-medium">
              {t("popup.error.button.ok")}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function StateTimeoutModal(props: { show: boolean; setShow: (show: boolean) => void }) {
  const { t } = useTranslation();

  const handleClose = () => {
    window.location.reload();
  };

  return (
    <Modal show={props.show} onHide={handleClose} size="sm" centered>
      <Modal.Header className="border-0 text-center">
        <Modal.Title className="w-100 fw-medium">
          <div className="text-center">
            <i className="fas fa-clock text-warning mb-2" style={{ fontSize: "2.5rem" }}></i>
            <div>{t("session-timeout.title")}</div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center pt-0">
        <p className="text-muted">{t("session-timeout.body")}</p>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="primary"
          onClick={handleClose}
          className="w-100 py-2 fw-medium"
          style={{
            transition: "all 0.15s ease-in-out",
          }}
        >
          {t("popup.error.button.ok")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function PasskeySuggestionModal(props: { show: boolean; onComplete: () => void }) {
  const { t } = useTranslation();
  const [registering, setRegistering] = useState(false);

  const handleCreate = async () => {
    setRegistering(true);
    try {
      await registerPasskey();
    } catch (err: any) {
      if (err.name !== "NotAllowedError") {
        console.error("error registering passkey from suggestion", err);
        HGBugsnagNotify("passkeyLoginSuggestion", err);
      }
    } finally {
      setRegistering(false);
      props.onComplete();
    }
  };

  return (
    <Modal show={props.show} onHide={props.onComplete} centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-medium">
          <PasskeyIcon size="1.2em" className="me-2" />
          {t("login.passkeys")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t("login.passkey-recommended")}</p>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={props.onComplete} disabled={registering}>
          {t("general.cancel")}
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={registering}>
          {registering && <Spinner animation="border" size="sm" className="me-2" />}
          <PasskeyIcon size="1.2em" className="me-1" />
          {t("login.sign-in-with-passkey")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function TwoFactorModal(props: {
  show: boolean;
  setShow: (show: boolean) => void;
  challenge: TwoFactorChallenge;
  setSuccess: (success: boolean) => void;
  setChallenge: (challenge?: TwoFactorChallenge) => void;
  loginEmail: string;
}) {
  const { t } = useTranslation();
  const [isEmail, setIsEmail] = useState(false);
  const email1CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email1, setEmail1] = useState("");
  const email2CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email2, setEmail2] = useState("");
  const email3CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email3, setEmail3] = useState("");
  const email4CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email4, setEmail4] = useState("");
  const email5CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email5, setEmail5] = useState("");
  const email6CodeBoxRef = useRef<HTMLInputElement>(null);
  const [email6, setEmail6] = useState("");
  const [emailCodeEntered, setEmailCodeEntered] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [emailCodeError, setEmailCodeError] = useState(false);

  useInterval(async () => {
    if (!props.show || !props.challenge || isEmail) return;
    // poll for updates to the challenge
    const req: TwoFactorChallengeStatusRequest = {
      token: props.challenge.token,
    };
    try {
      const resp = await authApi.twoFactorChallengeCompleted(req);
      if (resp.isEmail) {
        setIsEmail(true);
        return;
      }
      if (resp.expires && resp.xsrfToken) {
        // if successful, we have completed the 2fa login flow
        props.setSuccess(true);
        handleClose();
      }
    } catch (err: any) {
      switch (getStatusCode(err)) {
        case 403:
        case 404:
          // challenge expired or was never valid
          handleClose();
          break;
        default:
          console.error("get 2fa challenge status error", err);
      }
    }
  }, 1.5 * 1000);

  useEffect(() => {
    if (isEmail) {
      // if isEmail was set, set focus to the first input box
      email1CodeBoxRef.current?.focus();
    }
  }, [isEmail]);
  // these all serve to auto-focus on the next email code input box
  useEffect(() => {
    if (email1 && !email2) email2CodeBoxRef.current?.focus();
  }, [email1, email2]);
  useEffect(() => {
    if (email2 && !email3) email3CodeBoxRef.current?.focus();
  }, [email2, email3]);
  useEffect(() => {
    if (email3 && !email4) email4CodeBoxRef.current?.focus();
  }, [email3, email4]);
  useEffect(() => {
    if (email4 && !email5) email5CodeBoxRef.current?.focus();
  }, [email4, email5]);
  useEffect(() => {
    if (email5 && !email6) email6CodeBoxRef.current?.focus();
  }, [email5, email6]);

  const emailCode = (): string => {
    return email1 + email2 + email3 + email4 + email5 + email6;
  };

  const sendEmailCode = async (code: string) => {
    setShowSpinner(true);
    const req: TwoFactorChallengeResponse = {
      token: props.challenge.token,
      emailCode: code,
      uiCode: 0,
      loginEmail: props.loginEmail,
    };
    try {
      const resp = await authApi.twoFactorValidateEmail(req);
      if (resp.expires && resp.xsrfToken) {
        // if successful, we have completed the 2fa login flow
        props.setSuccess(true);
        handleClose();
      }
    } catch (err: any) {
      console.error("error validating email code", err);
      setEmailCodeError(true);
    } finally {
      setShowSpinner(false);
    }
  };

  if (isEmail && emailCode().length > 5 && !emailCodeEntered) {
    setEmailCodeEntered(true);
    sendEmailCode(emailCode()).then();
  } else if (isEmail && emailCodeEntered && emailCode().length <= 5) {
    setEmailCodeEntered(false);
    setEmailCodeError(false);
  }

  const sendEmail = async () => {
    try {
      const newChallenge = await authApi.twoFactorChallengeEmail(props.challenge);
      props.setChallenge(newChallenge);
      setIsEmail(true);
    } catch (err: any) {
      console.error("2fa challenge via email error", err);
      HGBugsnagNotify("2faEmail", err);
    }
  };

  const handleClose = () => {
    props.setChallenge(undefined);
    props.setShow(false);
    setIsEmail(false);
    setEmail1("");
    setEmail2("");
    setEmail3("");
    setEmail4("");
    setEmail5("");
    setEmail6("");
    setEmailCodeEntered(false);
    setShowSpinner(false);
    setEmailCodeError(false);
  };

  return (
    <Modal show={props.show} onHide={handleClose} backdrop="static" size="lg" centered>
      <Modal.Header className="border-0">
        <Modal.Title className="fw-medium">{t("form.button.login")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {isEmail ? (
          <>
            <Card className="border-0 shadow-sm">
              <Card.Header className="text-center border-0 py-3">
                <div className="mb-2">
                  <i className="fas fa-envelope text-primary" style={{ fontSize: "1.5rem" }}></i>
                </div>
                <h6 className="mb-0 fw-medium">{t("login.2fa.enter-email-code")}</h6>
              </Card.Header>
              <Card.Body className="py-4">
                <div className="d-flex justify-content-center gap-2 mb-3">
                  <Form.Control
                    onPaste={(e) => {
                      try {
                        const pasted = e.clipboardData.getData("text");
                        if (pasted.length === 6) {
                          setEmail1(pasted[0] ?? "");
                          setEmail2(pasted[1] ?? "");
                          setEmail3(pasted[2] ?? "");
                          setEmail4(pasted[3] ?? "");
                          setEmail5(pasted[4] ?? "");
                          setEmail6(pasted[5] ?? "");
                        }
                      } catch {
                        /*none*/
                      }
                    }}
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email1CodeBoxRef}
                    value={email1}
                    maxLength={1}
                    onChange={(e) => setEmail1(e.currentTarget.value)}
                  />
                  <Form.Control
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email2CodeBoxRef}
                    value={email2}
                    maxLength={1}
                    onChange={(e) => setEmail2(e.currentTarget.value)}
                  />
                  <Form.Control
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email3CodeBoxRef}
                    value={email3}
                    maxLength={1}
                    onChange={(e) => setEmail3(e.currentTarget.value)}
                  />
                  <Form.Control
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email4CodeBoxRef}
                    value={email4}
                    maxLength={1}
                    onChange={(e) => setEmail4(e.currentTarget.value)}
                  />
                  <Form.Control
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email5CodeBoxRef}
                    value={email5}
                    maxLength={1}
                    onChange={(e) => setEmail5(e.currentTarget.value)}
                  />
                  <Form.Control
                    autoComplete="off"
                    size="lg"
                    className="text-center fw-bold border-2"
                    style={{ width: "3.5rem", height: "3.5rem", fontSize: "1.25rem" }}
                    ref={email6CodeBoxRef}
                    value={email6}
                    maxLength={1}
                    onChange={(e) => setEmail6(e.currentTarget.value)}
                  />
                </div>
              </Card.Body>
              <Card.Footer className=" border-0 text-center">
                <small className="text-muted">{t("email.not-received.add-contacts")}</small>
              </Card.Footer>
            </Card>
          </>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Header className="text-center border-0 py-3">
              <div className="mb-2">
                <i className="fas fa-mobile-alt text-primary" style={{ fontSize: "1.5rem" }}></i>
              </div>
              <h6 className="mb-0 fw-medium">{t("login.2fa.enter-mobile-code")}</h6>
            </Card.Header>
            <Card.Body className="text-center py-4">
              <div className="display-4 fw-bold text-primary mb-3" style={{ fontFamily: "monospace" }}>
                {props.challenge.uiCode}
              </div>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 d-flex justify-content-between align-items-center">
        {emailCodeError && (
          <div className="flex-grow-1">
            <Danger text={t("userauth.feedback.validate-email-failure")} />
          </div>
        )}
        {showSpinner && <Spinner animation="border" className="me-3" />}
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleClose} className="px-4">
            {t("general.cancel")}
          </Button>
          {!isEmail && (
            <Button
              variant="primary"
              onClick={sendEmail}
              className="px-4"
              style={{
                transition: "all 0.15s ease-in-out",
              }}
            >
              <i className="fas fa-envelope me-2"></i>
              {t("userauth.form.label.email-code")}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
